import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Tab, Button } from '@material-ui/core';

import { ReturnRow } from 'types/ReturnTable';
import PreviousPurchases from 'containers/Forms/Customers/views/CustomerView/sales/PreviousPurchases';
import DateRangeInput, {
  DateRange,
} from 'containers/Forms/RecentSalesList/DateRangeInput';
import { getIsModuleEnabled, getSetting } from 'reducers/configs/settings';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import { previousModalPage } from 'actions/modalPage';
import { addWarning } from 'actions/Error';
import { SaleDocumentResponse } from 'types/SalesDocument';
import { TabContainer, TabPanel, Tabs } from 'components/Tabs';
import { useCollapseAllShortcut } from 'utils/hooks/keyboard/useCollapseAllShortcut';

import { formatDate, getSalesDocumentsWithReturnedRows } from './util';
import { useRowsExpandedState } from './useRowsExpandedState';

export { default } from 'containers/Forms/RecentSalesList/DateRangeInput';
export interface Sales {
  docs: SaleDocumentResponse[];
  returnedRows: ReturnRow[];
}

const emptySales: Sales = {
  docs: [],
  returnedRows: [],
};

const salesPerPage = 20;

export const RecentSalesList = ({ queryProps: props }) => {
  const [queryProps, setQueryProps] = useState(props);

  useEffect(() => {
    if (
      Object.entries(queryProps).some(([k, v]) => props[k] !== v) ||
      Object.entries(props).some(([k, v]) => queryProps[k] !== v)
    ) {
      setQueryProps(props);
    }
  }, [queryProps, props]);

  const { t } = useTranslation('customer');
  // Inputs
  const [dateRange, setDateRange] = useState<DateRange>({
    from: null,
    to: null,
  });

  const showName = queryProps?.showCustomer;
  const taxComponentsEnabled = useSelector(getIsModuleEnabled('subvatrates'));

  // Fetching state
  const [fetching, setFetching] = useState(false);
  const [done, setDone] = useState({
    invoices: false,
    invwaybills: false,
  });

  // Output
  const [sales, setSales] = useState<Sales>(emptySales);
  const [invWayBills, setInvWayBills] = useState<Sales>(emptySales);

  const options = ['invoices', 'invwaybills'] as const;

  const [activeTab, setActiveTab] = useState<typeof options[number]>(
    'invoices',
  );

  const [saleRowsExpanded, saleRowsExpandedActions] = useRowsExpandedState();
  const [
    invWaybillRowsExpanded,
    invWaybillRowsExpandedActions,
  ] = useRowsExpandedState();

  const currentLocationOnly = useSelector(
    getSetting('touchpos_recent_sales_only_from_current_location'),
  );
  const warehouseID = useSelector(getSelectedWarehouseID);
  const dispatch = useDispatch();

  const fetchSales = (first: boolean, type = 'invoices') => {
    if (currentLocationOnly && !warehouseID) {
      console.error(
        'There was an error with fetching the current warehouse. This can happen if menu is accessed too quickly. Please wait and retry',
      );
      dispatch(previousModalPage());
      dispatch(addWarning('Error fetching current warehouse. Please retry'));
      return;
    }
    setFetching(true);

    getSalesDocumentsWithReturnedRows({
      recordsOnPage: salesPerPage,
      getReturnedPayments: 1,
      presentCoupons: 1,
      types:
        type === 'invwaybills'
          ? 'INVWAYBILL'
          : 'CASHINVOICE,CREDITINVOICE,INVOICE',
      confirmed: 1,
      orderBy: type === 'invwaybills' ? 'lastChanged' : undefined,
      warehouseID: currentLocationOnly ? warehouseID : undefined,
      getRowsForAllInvoices: 1,
      dateFrom: formatDate(dateRange.from),
      dateTo: formatDate(dateRange.to),
      clientID: queryProps?.clientID,
      getTaxComponents: taxComponentsEnabled ? 1 : undefined, // most accounts don't have taxComponent module enabled, in this case we set the param to undefined
      pageNo: first
        ? 1
        : Math.floor(
            (type === 'invwaybills' ? invWayBills : sales).docs.length /
              salesPerPage,
          ) + 1, // 1-indexed
      ...queryProps,
    })
      .then(data => {
        const setData = s => ({
          docs: [...s.docs, ...data.docs],
          returnedRows: [...s.returnedRows, ...data.returnedRows],
        });
        if (type === 'invoices') {
          setSales(setData);
          const expandedState = Object.entries(saleRowsExpanded);
          const areAllExpanded = expandedState.every(
            ([, expanded]) => expanded,
          );
          // When fetching more preserve expanded state if all are expanded
          // When fetching a first page (applying new filter params) then collapse the results
          const newState = first
            ? false
            : areAllExpanded && expandedState.length > 0;
          saleRowsExpandedActions.setAll(
            Object.fromEntries(
              expandedState.concat(
                data.docs.map(doc => [String(doc.id), newState]),
              ),
            ),
          );
        }
        if (type === 'invwaybills') {
          setInvWayBills(setData);
          const expandedState = Object.entries(invWaybillRowsExpanded);
          const areAllExpanded = expandedState.every(
            ([, expanded]) => expanded,
          );
          // When fetching more preserve expanded state if all are expanded
          // When fetching a first page (applying new filter params) then collapse the results
          const newState = first
            ? false
            : areAllExpanded && expandedState.length > 0;
          invWaybillRowsExpandedActions.setAll(
            Object.fromEntries(
              expandedState.concat(
                data.docs.map(doc => [String(doc.id), newState]),
              ),
            ),
          );
        }
        if (data.docs.length !== salesPerPage) {
          setDone(prevState => ({
            ...prevState,
            [type]: true,
          }));
        }
      })
      .finally(() => setFetching(false));
  };

  const resetAndFetchFirst = (type = 'invoices') => {
    if (type === 'invwaybills') {
      setInvWayBills(emptySales);
      invWaybillRowsExpandedActions.setAll({});
    }
    if (type === 'invoices') {
      setSales(emptySales);
      saleRowsExpandedActions.setAll({});
    }
    setDone(prevState => ({
      ...prevState,
      [type]: false,
    }));
    fetchSales(true, type);
  };

  // Whenever query props are updated, start from scratch
  useEffect(() => {
    resetAndFetchFirst();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryProps]);

  useCollapseAllShortcut(
    activeTab === 'invwaybills'
      ? invWaybillRowsExpandedActions.setAllValues
      : saleRowsExpandedActions.setAllValues,
  );

  const handleTabSelection = type => {
    setActiveTab(type);
    if (type === 'invoices' && !sales.docs.length) {
      resetAndFetchFirst(type);
    } else if (type === 'invwaybills' && !invWayBills.docs.length) {
      resetAndFetchFirst(type);
    }
  };

  const onMore = (opt: string) => {
    if (
      queryProps?.limit !==
      (opt === 'invwaybills' ? invWayBills : sales).docs.length
    ) {
      fetchSales(false, opt);
    }
  };

  return (
    <TabContainer defaultTab="invoices">
      <Tabs id="recentSalesTabs" onSelect={handleTabSelection}>
        {options.map(opt => (
          <Tab
            key={opt}
            value={opt}
            label={t(`fields.tabs.${opt}`)}
            data-testid={`tab-${opt}`}
          />
        ))}
      </Tabs>
      {options.map(opt => {
        const expandedState = Object.entries(
          opt === 'invwaybills' ? invWaybillRowsExpanded : saleRowsExpanded,
        );

        const areAllExpanded = expandedState.every(([, expanded]) => expanded);
        return (
          <TabPanel key={opt} value={opt}>
            <Box
              display={!queryProps.showFilter ? 'none' : 'block'}
              marginTop="1rem"
            >
              <DateRangeInput
                titleFrom="from"
                titleTo="to"
                value={dateRange}
                onChange={setDateRange}
                dataTestKey="date-picker"
              />
              <Box display="flex" justifyContent="space-between" gridGap="1rem">
                <Button
                  color="primary"
                  variant="contained"
                  disableElevation
                  data-testid="filter-purchases"
                  onClick={() => resetAndFetchFirst(opt)}
                >
                  {t('customerView.buttons.filter')}
                </Button>
                <Button
                  variant="text"
                  data-testid="expand-all"
                  onClick={() => {
                    const setAllValues =
                      opt === 'invwaybills'
                        ? invWaybillRowsExpandedActions.setAllValues
                        : saleRowsExpandedActions.setAllValues;
                    setAllValues(!areAllExpanded && !!expandedState.length);
                  }}
                >
                  {areAllExpanded && !!expandedState.length
                    ? t('customerView.buttons.collapseAllRows')
                    : t('customerView.buttons.expandAllRows')}
                </Button>
              </Box>
            </Box>
            <PreviousPurchases
              title={t('customerView.purchases.title')}
              sales={opt === 'invwaybills' ? invWayBills : sales}
              name={showName ?? false}
              loading={fetching ? t('customerView.loading') : ''}
              done={!queryProps.morePages || done[opt]}
              onMore={() => onMore(opt)}
              showFilter={queryProps.showFilter}
              getRowExpandedState={
                (opt === 'invwaybills'
                  ? invWaybillRowsExpandedActions
                  : saleRowsExpandedActions
                ).get
              }
              setRowExpandedState={
                (opt === 'invwaybills'
                  ? invWaybillRowsExpandedActions
                  : saleRowsExpandedActions
                ).set
              }
            />
          </TabPanel>
        );
      })}
    </TabContainer>
  );
};
